import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import axiosInstance, { sharedGet } from '../services/api';
import { useAuth } from './AuthContext';

// Create the context
const SystemSettingsContext = createContext();

// Default settings
const DEFAULT_SETTINGS = {
  timezone: 'America/Los_Angeles',
  businessName: 'Chemical Monitoring System',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  currency: 'USD',
  volumeUnit: 'gallons'
};

// Cache for location-specific timezones to avoid repeated API calls
const locationTimezoneCache = new Map();

// Provider component
export function SystemSettingsProvider({ children }) {
  const { user: profile, supportPicking } = useAuth(); // AC1: wait for the profile; skip while a support login picks an account
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserUid, setCurrentUserUid] = useState(null);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const previousUserUid = useRef(null);
  const previousLocationId = useRef(null);

  // Define fetchSettings BEFORE the useEffects that use it
  const fetchSettings = useCallback(async (locationId = null) => {
    try {
      setLoading(true);

      // Skip if not authenticated
      const user = auth.currentUser;
      if (!user) {

        setSettings(DEFAULT_SETTINGS);
        setLoading(false);
        return;
      }

      // Include locationId in query if provided for location-specific timezone
      const url = locationId
        ? `/system-settings?locationId=${locationId}`
        : `/system-settings`;

      // Through axiosInstance, so developer org header (x-developer-org-id) is sent automatically.
      // sharedGet: the user and location effects both fire on a location change (PERF2); one request.
      const response = await sharedGet(url);

      if (response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });

        // Cache the location timezone for quick access
        if (locationId && response.data.timezone) {
          locationTimezoneCache.set(locationId, response.data.timezone);
        }
      } else {
        console.warn('Failed to fetch system settings, using defaults');
      }
    } catch (err) {
      console.error('Error fetching system settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (updates, locationId = null) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Include locationId in query if provided for location-specific timezone update
      const url = locationId
        ? `/system-settings?locationId=${locationId}`
        : `/system-settings`;

      // Use axiosInstance so developer org header is sent automatically
      const response = await axiosInstance.put(url, updates);

      if (response.data) {
        const result = response.data;
        // Update settings if not location-specific, otherwise just clear cache
        if (!locationId && result.settings) {
          setSettings({ ...DEFAULT_SETTINGS, ...result.settings });
        } else if (locationId && updates.timezone) {
          // Update cache and current settings if this is the current location
          locationTimezoneCache.set(locationId, updates.timezone);
          if (currentLocationId === locationId) {
            setSettings(prev => ({ ...prev, timezone: updates.timezone }));
          }
        }
        return { success: true };
      } else {
        return { success: false, error: 'Failed to update settings' };
      }
    } catch (err) {
      console.error('Error updating system settings:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      return { success: false, error: errorMessage };
    }
  }, [currentLocationId]);

  // Listen for auth state changes to detect user switches
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const newUid = user?.uid || null;

      // Detect user change (including logout)
      if (previousUserUid.current !== newUid) {

        // Reset to defaults immediately on user change
        if (previousUserUid.current !== null || newUid === null) {
          setSettings(DEFAULT_SETTINGS);
          setLoading(true);
        }

        previousUserUid.current = newUid;
      }

      setCurrentUserUid(newUid);
    });

    return () => unsubscribe();
  }, []);

  // Fetch settings when user changes
  useEffect(() => {
    if (currentUserUid && (!profile || supportPicking)) return;
    if (currentUserUid) {
      fetchSettings(currentLocationId);
    } else {
      // No user - use defaults
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
    }
  }, [currentUserUid, fetchSettings, currentLocationId, profile, supportPicking]);

  // Refetch settings when location changes
  useEffect(() => {
    if (currentUserUid && profile && !supportPicking && currentLocationId && currentLocationId !== previousLocationId.current) {

      previousLocationId.current = currentLocationId;
      fetchSettings(currentLocationId);
    }
  }, [currentLocationId, currentUserUid, fetchSettings, profile, supportPicking]);

  useEffect(() => {
    if (!currentUserUid || !profile || supportPicking) return undefined;

    const refreshOnFocus = () => fetchSettings(currentLocationId);
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [currentLocationId, currentUserUid, fetchSettings, profile, supportPicking]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    settings,
    loading,
    error,
    updateSettings,
    refreshSettings: fetchSettings,
    timezone: settings.timezone || DEFAULT_SETTINGS.timezone,
    volumeUnit: settings.volumeUnit || DEFAULT_SETTINGS.volumeUnit,
    // Location-aware features
    currentLocationId,
    setCurrentLocationId,
    // Get cached timezone for a location (for quick access without API call)
    getLocationTimezone: (locationId) => locationTimezoneCache.get(locationId) || settings.timezone
  }), [settings, loading, error, updateSettings, fetchSettings, currentLocationId]);

  return (
    <SystemSettingsContext.Provider value={value}>
      {children}
    </SystemSettingsContext.Provider>
  );
}

// Hook to use system settings
export function useSystemSettings() {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return context;
}

// Export the context for direct use if needed
export default SystemSettingsContext;
